import {useRef} from 'react'
import {FormControl, TextInput} from '@primer/react'
import {Stack} from '@primer/react/experimental'

import {CommandButton, ScopedCommands} from '@github-ui/ui-commands'

export const MaxPRTitleLength = 256
export const BlankPRTitleError = "Title can't be blank"
export const EditTitleFormLabel = 'Edit pull request title'

export function maxPRTitleLengthError(title: string) {
  return `Title can't be longer than ${MaxPRTitleLength} characters (currently ${title.length} characters)`
}

function isTitleValid(title: string) {
  return title.trim().length > 0 && title.length <= MaxPRTitleLength
}

export interface SubmitEditTitleFormParams {
  title: string
  expectedTitle: string
}

export interface PullRequestEditTitleFormProps {
  isDirty: boolean
  value: string
  onChange(value: string): void
  onCancel(): void
  onSubmit(): void
  submitError?: Error | null
}

export function PullRequestEditTitleForm({
  isDirty,
  value,
  onChange,
  onCancel,
  onSubmit,
  submitError,
}: PullRequestEditTitleFormProps) {
  const isValid = !isDirty || isTitleValid(value)

  const editTitleInputRef = useRef<HTMLInputElement>(null)

  const onTitleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)

  const submitErrorMessage = submitError?.message ?? null

  const handleSubmit = () => {
    // Prevent submitting invalid title
    if (!isValid) {
      editTitleInputRef.current?.focus()
      return
    }

    // Just close the form if title is valid but unchanged
    if (isValid && !isDirty) {
      onCancel()
      return
    }

    onSubmit()
  }

  const handleFormSubmit = (e: React.SubmitEvent) => {
    e.preventDefault()
    handleSubmit()
  }

  return (
    <ScopedCommands
      commands={{
        'github:submit-form': handleSubmit,
        'github:submit-input': handleSubmit,
        'github:cancel': onCancel,
      }}
    >
      <Stack
        direction={{narrow: 'vertical', regular: 'horizontal'}}
        gap="condensed"
        as="form"
        onSubmit={handleFormSubmit}
      >
        <Stack.Item grow>
          <FormControl>
            <FormControl.Label visuallyHidden>{EditTitleFormLabel}</FormControl.Label>
            <TextInput
              autoFocus
              block
              className="f4"
              onChange={onTitleInputChange}
              ref={editTitleInputRef}
              validationStatus={!isValid ? 'error' : undefined}
              value={value}
            />
            <DisplayError isValid={isValid} saveErrorMessage={submitErrorMessage} title={value} />
          </FormControl>
        </Stack.Item>
        <Stack direction="horizontal" gap="condensed">
          <CommandButton commandId="github:cancel">Cancel</CommandButton>
          <CommandButton commandId="github:submit-input" variant="primary" showKeybindingHint>
            Save
          </CommandButton>
        </Stack>
      </Stack>
    </ScopedCommands>
  )
}

function DisplayError({
  isValid,
  saveErrorMessage,
  title,
}: {
  isValid: boolean
  saveErrorMessage: string | null
  title: string
}) {
  function titleValidationMessage() {
    const emptyTitle = title.trim().length === 0
    if (emptyTitle) {
      return BlankPRTitleError
    } else if (title.length > MaxPRTitleLength) {
      return maxPRTitleLengthError(title)
    }
  }

  if (!isValid) {
    return <FormControl.Validation variant="error">{titleValidationMessage()}</FormControl.Validation>
  } else if (saveErrorMessage) {
    return <FormControl.Validation variant="error">{saveErrorMessage}</FormControl.Validation>
  } else {
    return null
  }
}
